/**
 * AdminSatisRaporlama.jsx
 *
 * Danışman CRM Faz 1 — Satış Raporlama (admin-only).
 * Route: /admin/satis-raporlama
 *
 * Backend: GET /admin/satis-raporlama?range=today|week|month|year&sort=amount|count
 *          GET /admin/hakedis-ozet?donem=YYYY-MM  (Hakediş toggle için)
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AdminLayout from '../components/admin/AdminLayout';
import PageHeader from '../components/admin/PageHeader';
import HakedisDetayTablo from '../components/admin/HakedisDetayTablo';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import { fmtMoney } from '../utils/money';
import { trNowLocalMonthInput } from '../utils/trDateTime';
import { Skeleton } from '../components/ui/skeleton';
import { BarChart3, Wallet, ShoppingCart, Users2, AlertTriangle, RefreshCw, ChevronDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RANGE_OPTIONS = [
  { value: 'today', tr: 'Bugün', en: 'Today' },
  { value: 'week', tr: 'Bu Hafta', en: 'This Week' },
  { value: 'month', tr: 'Bu Ay', en: 'This Month' },
  { value: 'year', tr: 'Bu Yıl', en: 'This Year' },
];

const SORT_TOGGLE = [
  { value: 'amount', tr: 'Tutar', en: 'Amount' },
  { value: 'count', tr: 'Adet', en: 'Count' },
  { value: 'hakedis', tr: 'Hakediş', en: 'Earnings' },
];

const FOCUS_CLS = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1';

function currentMonthStr() {
  return trNowLocalMonthInput();
}

function KpiCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-white p-4" style={{ borderRadius: 14, border: '1px solid var(--crm-card-border)' }}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon size={14} style={{ color: 'var(--crm-navy)' }} />
        <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: 'var(--crm-muted-2)' }}>{label}</span>
      </div>
      <p className="text-xl font-bold" style={{ color: 'var(--crm-navy-text)' }}>{value}</p>
    </div>
  );
}

function BarRow({ label, valueLabel, pct, tr, noRule, noRuleAmount }) {
  // Hakediş modunda kural_var:false gelen satırlar için: ₺0 çubuk yerine
  // "Kural tanımlanmadı" rozeti + (varsa) kural tanımsız tutar bilgisi.
  if (noRule) {
    return (
      <div className="mb-2.5">
        <div className="flex items-center justify-between gap-2 mb-1 text-xs">
          <span className="font-semibold truncate" style={{ color: 'var(--crm-navy-text)' }}>{label}</span>
          <span
            className="px-2 py-0.5 rounded-full text-[10px] font-bold flex-shrink-0"
            style={{ backgroundColor: 'rgba(160,61,44,0.08)', color: 'var(--crm-danger)' }}
          >
            {tr('Kural tanımlanmadı', 'No rule defined')}
          </span>
        </div>
        {noRuleAmount > 0 && (
          <p className="text-[11px] font-semibold" style={{ color: 'var(--crm-muted-2)' }}>
            {tr('Kural tanımsız tutar', 'Amount without a defined rule')}: {fmtMoney(noRuleAmount)}
          </p>
        )}
      </div>
    );
  }
  return (
    <div className="mb-2.5">
      <div className="flex items-center justify-between mb-1 text-xs">
        <span className="font-semibold truncate" style={{ color: 'var(--crm-navy-text)' }}>{label}</span>
        <span className="font-bold flex-shrink-0" style={{ color: 'var(--crm-navy-text)' }}>{valueLabel}</span>
      </div>
      <div
        className="h-2.5 rounded-full overflow-hidden"
        style={{ backgroundColor: 'var(--crm-cream)' }}
        role="img"
        aria-label={tr(`${label}: %${pct.toFixed(1)}, ${valueLabel}`, `${label}: %${pct.toFixed(1)}, ${valueLabel}`)}
      >
        <div
          className="h-full rounded-full"
          style={{ width: `${Math.max(pct, 2)}%`, background: 'linear-gradient(90deg, #C9A227, #0E1F3D)' }}
        />
      </div>
    </div>
  );
}

export default function AdminSatisRaporlama() {
  const { lang } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();
  const tr = (t, e) => (lang === 'tr' ? t : e);

  const [range, setRange] = useState('today');
  const [sortMode, setSortMode] = useState('amount');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const [hakedisData, setHakedisData] = useState(null);
  const [hakedisLoading, setHakedisLoading] = useState(false);
  const [expandedDanisman, setExpandedDanisman] = useState(null);

  const fetchReport = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const sortParam = sortMode === 'hakedis' ? 'amount' : sortMode;
      const r = await axios.get(`${API}/admin/satis-raporlama?range=${range}&sort=${sortParam}`, { headers: authHeaders() });
      setData(r.data);
    } catch (err) { setError(true); toast.error(getApiErrorMessage(err, lang)); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, sortMode, lang]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  useEffect(() => {
    if (sortMode !== 'hakedis') return;
    setHakedisLoading(true);
    axios.get(`${API}/admin/hakedis-ozet?donem=${currentMonthStr()}`, { headers: authHeaders() })
      .then((r) => setHakedisData(r.data))
      .catch((err) => toast.error(getApiErrorMessage(err, lang)))
      .finally(() => setHakedisLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortMode]);

  // Danışman sıralama satırları — toggle'a göre kaynak + değer seçimi
  let danismanRows = [];
  if (sortMode === 'hakedis') {
    danismanRows = (hakedisData?.danismanlar || []).map((d) => ({
      key: d.danisman_id, label: d.danisman_adi || d.danisman_id, value: d.hakedis_toplam,
      kuralVar: d.kural_var !== false,
      tanimsizTutar: d.tanimsiz_gun_tutari || 0,
      kademeDetay: d.kademe_detay || [],
      detay: d.detay || [],
      pencereDisiTahsilat: d.pencere_disi_tahsilat || 0,
    }));
  } else if (data?.danisman_siralama) {
    danismanRows = data.danisman_siralama.map((d) => ({
      key: d.danisman_id,
      label: d.danisman_adi || d.danisman_id,
      value: sortMode === 'amount' ? d.toplam_tutar : d.satis_adedi,
    }));
  }
  const danismanTotal = danismanRows.reduce((s, r) => s + Number(r.value || 0), 0);

  const egitimRows = data?.egitim_dagilimi || [];
  const egitimTotal = egitimRows.reduce((s, r) => s + Number(r.toplam_tutar || 0), 0);

  const isLoadingList = sortMode === 'hakedis' ? hakedisLoading : loading;

  return (
    <AdminLayout>
      <div className="-m-4 lg:-m-6 p-4 lg:p-6 min-h-full" style={{ backgroundColor: 'var(--crm-cream)' }}>
        <PageHeader
          breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: tr('Satış Raporlama', 'Sales Reporting') }]}
          title={tr('Satış Raporlama', 'Sales Reporting')}
          subtitle={tr('Dönem bazlı ciro, danışman sıralaması ve eğitim dağılımı', 'Period-based revenue, consultant ranking and course distribution')}
        />

        {/* Aralık pill'leri */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          {RANGE_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              disabled={sortMode === 'hakedis'}
              aria-disabled={sortMode === 'hakedis'}
              onClick={() => setRange(o.value)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-bold border transition-colors ${FOCUS_CLS} ${sortMode === 'hakedis' ? 'opacity-40 cursor-not-allowed' : ''}`}
              style={{ outlineColor: 'var(--crm-navy)', ...(range === o.value
                ? { backgroundColor: 'var(--crm-navy)', color: 'white', borderColor: 'var(--crm-navy)' }
                : { backgroundColor: 'white', color: 'var(--crm-navy-text)', borderColor: 'var(--crm-card-border)' }) }}
            >
              {lang === 'tr' ? o.tr : o.en}
            </button>
          ))}
        </div>

        {/* Hata */}
        {error && !loading && (
          <div className="mb-4 p-3 rounded-xl flex items-center justify-between gap-3" style={{ backgroundColor: 'rgba(160,61,44,0.06)', border: '1px solid rgba(160,61,44,0.25)' }}>
            <span className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--crm-danger)' }}>
              <AlertTriangle size={16} /> {tr('Bağlantı koptu — tekrar dene', 'Connection lost — try again')}
            </span>
            <button onClick={fetchReport} className="px-3 py-1.5 rounded-lg text-xs font-bold text-white flex items-center gap-1" style={{ backgroundColor: 'var(--crm-danger)' }}>
              <RefreshCw size={13} /> {tr('Tekrar Dene', 'Retry')}
            </button>
          </div>
        )}

        {/* KPI 4'lü */}
        {loading && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white p-4" style={{ borderRadius: 14, border: '1px solid var(--crm-card-border)' }}>
                <Skeleton className="h-3 w-2/3 mb-2" /><Skeleton className="h-6 w-1/2" />
              </div>
            ))}
          </div>
        )}
        {!loading && !error && data && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <KpiCard icon={Wallet} label={tr('Toplam Ciro', 'Total Revenue')} value={fmtMoney(data.toplam_ciro)} />
            <KpiCard icon={ShoppingCart} label={tr('Toplam Satış', 'Total Sales')} value={data.toplam_satis} />
            <KpiCard icon={BarChart3} label={tr('Ortalama Sepet', 'Average Basket')} value={fmtMoney(data.ortalama_sepet)} />
            <KpiCard icon={Users2} label={tr('Aktif Danışman', 'Active Consultants')} value={data.aktif_danisman_sayisi} />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Danışman Sıralaması */}
          <div className="bg-white p-4" style={{ borderRadius: 14, border: '1px solid var(--crm-card-border)' }}>
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h3 className="font-bold text-sm" style={{ color: 'var(--crm-navy-text)' }}>{tr('Danışman Sıralaması', 'Consultant Ranking')}</h3>
              <div className="flex gap-1">
                {SORT_TOGGLE.map((o) => (
                  <button
                    key={o.value}
                    onClick={() => setSortMode(o.value)}
                    className={`px-2.5 py-1 rounded-lg text-[11px] font-bold border ${FOCUS_CLS}`}
                    style={{ outlineColor: 'var(--crm-navy)', ...(sortMode === o.value
                      ? { backgroundColor: 'var(--crm-navy)', color: 'white', borderColor: 'var(--crm-navy)' }
                      : { backgroundColor: 'white', color: 'var(--crm-navy-text)', borderColor: 'var(--crm-card-border)' }) }}
                  >
                    {lang === 'tr' ? o.tr : o.en}
                  </button>
                ))}
              </div>
            </div>
            {sortMode === 'hakedis' && (
              <p className="text-[11px] font-semibold mb-2" style={{ color: 'var(--crm-muted-2)' }}>
                {tr(`Hakediş cari ay (${currentMonthStr()}) bazlıdır`, `Earnings are based on the current month (${currentMonthStr()})`)}
              </p>
            )}
            {isLoadingList && <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-6 w-full" />)}</div>}
            {!isLoadingList && danismanRows.length === 0 && (
              <p className="text-xs" style={{ color: 'var(--crm-muted)' }}>{tr('Bu aralıkta satış kaydı yok.', 'No sales recorded in this range.')}</p>
            )}
            {!isLoadingList && danismanRows.map((r) => {
              const hasDetail = sortMode === 'hakedis' && (r.kademeDetay?.length > 0 || r.detay?.length > 0 || r.pencereDisiTahsilat > 0);
              const isExpanded = expandedDanisman === r.key;
              return (
                <div key={r.key}>
                  <div className="flex items-center gap-1">
                    <div className="flex-1">
                      <BarRow
                        label={r.label}
                        valueLabel={sortMode === 'count' ? String(r.value) : fmtMoney(r.value)}
                        pct={danismanTotal > 0 ? (r.value / danismanTotal) * 100 : 0}
                        tr={tr}
                        noRule={sortMode === 'hakedis' && r.kuralVar === false}
                        noRuleAmount={r.tanimsizTutar}
                      />
                    </div>
                    {hasDetail && (
                      <button
                        type="button"
                        onClick={() => setExpandedDanisman(isExpanded ? null : r.key)}
                        aria-label={isExpanded ? tr('Detayı gizle', 'Hide detail') : tr('Detayı göster', 'Show detail')}
                        className="p-1 rounded flex-shrink-0 mb-2.5"
                        style={{ color: 'var(--crm-info)' }}
                      >
                        <ChevronDown size={14} className={isExpanded ? 'rotate-180 transition-transform' : 'transition-transform'} />
                      </button>
                    )}
                  </div>
                  {hasDetail && isExpanded && (
                    <div className="mb-2.5 -mt-1.5 px-2">
                      <HakedisDetayTablo
                        kademeDetay={r.kademeDetay}
                        detay={r.detay}
                        pencereDisiTahsilat={r.pencereDisiTahsilat}
                        tr={tr}
                        lang={lang}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Eğitim Bazında Dağılım */}
          <div className="bg-white p-4" style={{ borderRadius: 14, border: '1px solid var(--crm-card-border)' }}>
            <h3 className="font-bold text-sm mb-3" style={{ color: 'var(--crm-navy-text)' }}>{tr('Eğitim Bazında Dağılım', 'Distribution by Course')}</h3>
            {loading && <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-6 w-full" />)}</div>}
            {!loading && egitimRows.length === 0 && (
              <p className="text-xs" style={{ color: 'var(--crm-muted)' }}>{tr('Bu aralıkta satış kaydı yok.', 'No sales recorded in this range.')}</p>
            )}
            {!loading && egitimRows.map((r) => (
              <BarRow
                key={r.course_id || r.egitim_adi}
                label={r.egitim_adi}
                valueLabel={fmtMoney(r.toplam_tutar)}
                pct={egitimTotal > 0 ? (r.toplam_tutar / egitimTotal) * 100 : 0}
                tr={tr}
              />
            ))}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
